import { useEffect } from "react";
import { useAuth } from "@/lib/AuthContext";
import { api } from "@/lib/api";

// Bundle K — Single source of truth that pulls the user's saved
// Appearance prefs from the backend (`/api/settings/appearance`) and
// applies them to the `:root` element so every consumer downstream
// (CSS variables, density media-rules, motion overrides) reflects the
// user's choices on every page load AND on every settings change.
//
// Pre-Bundle-K, AppearanceSection wrote to backend successfully but no
// component on the boot path read it back, so font-family / font-size /
// density / reduced-motion changes silently disappeared on refresh.
//
// Listens for `mmntm:appearance-changed` window events so AppearanceSection
// can request immediate re-application without forcing a reload.

const FONT_STACKS = {
  system: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  inter: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  geist: "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  space_grotesk: "'Space Grotesk', -apple-system, 'Segoe UI', sans-serif",
  jetbrains_mono: "'JetBrains Mono', source-code-pro, Menlo, Monaco, Consolas, monospace",
  manrope: "'Manrope', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
};

const FONT_BASE_PX = {
  small: "14px",
  default: "16px",
  large: "18px",
};

const VALID_DENSITIES = new Set(["compact", "comfortable", "spacious"]);
const VALID_MOTION = new Set(["system", "full", "reduce"]);

function applyToRoot(prefs) {
  const root = document.documentElement;
  if (!prefs) return;

  // Font family — fall back to manrope so legacy users see the same body.
  const family = FONT_STACKS[prefs.font_family] || FONT_STACKS.manrope;
  root.style.setProperty("--tm-font-family", family);

  // Font base size.
  const px = FONT_BASE_PX[prefs.font_size] || FONT_BASE_PX.default;
  root.style.setProperty("--tm-font-base", px);

  // Density — migrate the stale `comfort` value (CommandBar wrote it
  // pre-Bundle-K) to canonical `comfortable` so the data-density
  // attribute matches the AppearanceSection slugs and the index.css
  // selectors. Idempotent — runs every boot.
  let density = prefs.density;
  if (density === "comfort") density = "comfortable";
  if (!VALID_DENSITIES.has(density)) density = "comfortable";
  root.setAttribute("data-density", density);

  // Motion. `system` removes the explicit attribute so the OS
  // `prefers-reduced-motion` media query is the only authority.
  let motion = prefs.reduced_motion;
  if (!VALID_MOTION.has(motion)) motion = "system";
  if (motion === "system") root.removeAttribute("data-motion");
  else root.setAttribute("data-motion", motion);
}

export default function AppearanceApplier() {
  const { user } = useAuth();

  // Hydrate from backend on user-mount. The first run uses the default
  // shape if the user has never saved anything, ensuring `--tm-font-base`
  // is always set for the body { font-size: var(--tm-font-base, 16px) }
  // rule to resolve sensibly.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        const prefs = await api.getAppearance();
        if (!cancelled) applyToRoot(prefs);
      } catch {
        applyToRoot({});  // apply defaults silently — never block the UI
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  // Listen for in-session changes from AppearanceSection so updates
  // apply instantly without needing a page reload.
  useEffect(() => {
    const onChanged = (e) => applyToRoot(e?.detail || {});
    window.addEventListener("mmntm:appearance-changed", onChanged);
    return () => window.removeEventListener("mmntm:appearance-changed", onChanged);
  }, []);

  // One-shot stale-localStorage migration. CommandBar's quick toggle
  // wrote `mmntm-density="comfort"` before Bundle K. Migrate to the
  // canonical slug so the toggle and AppearanceSection agree.
  useEffect(() => {
    try {
      if (localStorage.getItem("mmntm-density") === "comfort") {
        localStorage.setItem("mmntm-density", "comfortable");
      }
    } catch { /* private mode etc. — ignore */ }
  }, []);

  return null;
}

export { applyToRoot };
